import { Loader2 } from 'lucide-react';
import { isBadSendOutcome, sendOutcomeLabel, type InvoiceSend } from '@jobos/shared';
import { useInvoiceSends } from '@/hooks/useInvoiceSends';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * The send log, exactly as recorded — a sibling of PaymentHistoryList for the
 * same invoice's email attempts rather than its payments.
 *
 * `outcomeDetail` is written unconditionally by the webhook (a transient bounce
 * leaves a detail even once a later `email.delivered` moves the state), but it
 * is DISPLAYED only when `isBadSendOutcome(state)` is true — see the rule on
 * that function in shared/utils/invoiceSendState.ts. Ungated, a row reading
 * "Delivered" could still carry "mailbox full" underneath it.
 */
interface SendHistoryListProps {
  invoiceId: string;
  /**
   * Opens the archived copy of one attempt (NEW-238).
   *
   * `sentOn` is the send's date as `YYYY-MM-DD`, derived HERE rather than by
   * the viewer: the server names the archived object
   * `<invoiceNumber>-sent-<sentOn>.pdf` from the same `created_at`, and the
   * browser cannot read that name back (Content-Disposition is not in the
   * API's CORS exposeHeaders), so the caller has to rebuild it. Passing the
   * date out of the row that was clicked is what keeps the two from drifting.
   */
  onViewSentPdf: (sendId: string, sentOn: string) => void;
}

const formatSendDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

export function SendHistoryList({ invoiceId, onViewSentPdf }: SendHistoryListProps) {
  const { data: sends, isLoading } = useInvoiceSends(invoiceId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!sends?.length) {
    return <p className="text-sm text-gray-500 py-2">No emails sent yet.</p>;
  }

  return (
    <ul className="divide-y divide-gray-100">
      {sends.map((send) => (
        <SendHistoryRow key={send.id} send={send} onViewSentPdf={onViewSentPdf} />
      ))}
    </ul>
  );
}

function SendHistoryRow({
  send,
  onViewSentPdf,
}: {
  send: InvoiceSend;
  onViewSentPdf: (sendId: string, sentOn: string) => void;
}) {
  // Gate: the write is unconditional, the display is not (D5).
  const bad = isBadSendOutcome(send.state);
  const label = sendOutcomeLabel(send);

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm text-gray-900">
            <span className="text-gray-500">{formatSendDate(send.createdAt)}</span>
            <span className="truncate">{send.recipients.join(', ')}</span>
          </div>
          <div className="mt-0.5 text-xs text-gray-500">
            Sent by {send.createdByName ?? 'the system'}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-0.5">
          <span className={cn('text-sm', bad ? 'text-red-600' : 'text-gray-500')}>{label}</span>

          {/* Nothing is rendered when hasSentPdf is false: a send whose bytes
              were never kept (every attempt before NEW-238) must not advertise
              a copy — the link would only 404. */}
          {send.hasSentPdf && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              // UTC via toISOString, exactly as loadSentPdf does server-side —
              // the same instant through the same derivation, so the name the
              // viewer shows and saves is the name the response carries.
              onClick={() => onViewSentPdf(send.id, new Date(send.createdAt).toISOString().slice(0, 10))}
            >
              View copy
            </Button>
          )}
        </div>
      </div>

      {bad && send.outcomeDetail && (
        <p className="mt-1 text-xs text-red-600">{send.outcomeDetail}</p>
      )}
    </li>
  );
}
